import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HierarchyFilterComponent } from '../../../components/FilterBar/HierarchyFilter';
import { StockLevelChart } from '../../../components/charts/StockLevelChart';
import { CriticalStockMachinesTable } from '../../../components/tables/CriticalStockMachinesTable';
import { CriticalStockProductsTable } from '../../../components/tables/CriticalStockProductsTable';
import { HierarchyFilter, StockReportingData, StockFilter as StockFilterType } from '../../../lib/types';
import { subDays } from 'date-fns';

export function StockReporting() {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [filter, setFilter] = useState<HierarchyFilter>({
    dateFrom: subDays(new Date(), 30).toISOString(),
    dateTo: new Date().toISOString(),
    granularity: 'day',
    compareToPrevious: false,
    brandScope: 'sku_or_campaign',
  });

  const [data, setData] = useState<StockReportingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [stockFilter, setStockFilter] = useState<StockFilterType>({
    operatorIds: [],
    machineIds: [],
    productIds: []
  });
  const [chartViewMode, setChartViewMode] = useState<'average' | 'granular'>('average');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        setUser(data.user);
      });
    }
  }, []);

  useEffect(() => {
    if (user?.tenantId) {
      // Debounce filter changes to prevent rapid API calls and scroll issues
      const timeoutId = setTimeout(() => {
        fetchStockData();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [user, filter, stockFilter]);

  const fetchStockData = async () => {
    try {
      // Preserve scroll position
      const scrollY = window.scrollY;
      
      setLoading(true);
      const params = new URLSearchParams({
        dateFrom: filter.dateFrom,
        dateTo: filter.dateTo,
        granularity: filter.granularity,
        compareToPrevious: String(filter.compareToPrevious || false),
        brandScope: filter.brandScope || 'sku_or_campaign',
      });

      // Add multi-select filter parameters
      if (stockFilter.operatorIds.length > 0) {
        stockFilter.operatorIds.forEach(id => params.append('operatorIds', id));
      }
      if (stockFilter.machineIds.length > 0) {
        stockFilter.machineIds.forEach(id => params.append('machineIds', id));
      }
      if (stockFilter.productIds.length > 0) {
        stockFilter.productIds.forEach(id => params.append('productIds', id));
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`/api/brand/${user.tenantId}/dashboard/stock?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch stock data');
      }

      const data = await response.json();
      setData(data);
      
      // Restore scroll position after React re-renders
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY);
      });
    } catch (error) {
      console.error('Error fetching stock data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    console.log('Export CSV');
  };

  const handleExportPNG = () => {
    console.log('Export PNG');
  };

  const handleExportPagePDF = () => {
    console.log('Export Page PDF');
  };

  const handleSeeAllMachines = () => {
    navigate(`/brand/${brandId}/dashboard/stock/machines/all`);
  };

  const handleSeeAllProducts = () => {
    navigate(`/brand/${brandId}/dashboard/stock/products/all`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-red-600">Failed to load data</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Filter Section with proper separation */}
      <div className="bg-white shadow-sm">
        <HierarchyFilterComponent
          filter={filter}
          onChange={setFilter}
          onExportCSV={handleExportCSV}
          onExportPNG={handleExportPNG}
          onExportPagePDF={handleExportPagePDF}
          showBrandScope={true}
        />
      </div>

      {/* Main Content with proper spacing */}
      <div className="max-w-7xl mx-auto p-6 pt-8" style={{ scrollBehavior: 'auto' }}>
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Stock Reporting</h1>
          <p className="text-gray-600">Monitor stock levels over time and identify critical stock situations</p>
        </div>

        {/* Stock Level Graph with Integrated Filter */}
        <div className="mb-8">
          <StockLevelChart 
            data={data.stockLevelTimeSeries}
            salesData={data.totalSalesTimeSeries}
            granularData={data.stockLevelGranular}
            viewMode={chartViewMode}
            onViewModeChange={setChartViewMode}
            stockFilter={stockFilter}
            onStockFilterChange={setStockFilter}
            operators={data?.operators || []}
            machines={data?.machines || []}
            products={data?.products || []}
          />
        </div>

        {/* Critical Stock Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CriticalStockMachinesTable 
            data={data.criticalStockMachines} 
            onSeeAll={handleSeeAllMachines}
          />
          <CriticalStockProductsTable 
            data={data.criticalStockProducts} 
            onSeeAll={handleSeeAllProducts}
          />
        </div>
      </div>
    </div>
  );
}
